import React, { useState, useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

// A zoomable image lightbox. Click image to toggle 2x zoom; move mouse to pan.
const Lightbox = ({ images = [], captions = [], open, index = 0, onClose, title }) => {
  const [current, setCurrent] = useState(index);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  useEffect(() => setCurrent(index), [index, open]);
  useEffect(() => setZoom(false), [current]);

  const next = useCallback(() => setCurrent((c) => (c + 1) % images.length), [images.length]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, onClose]);

  if (!open) return null;

  const move = (e) => {
    if (!zoom) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <div className="fixed inset-0 z-[10001] flex flex-col bg-[rgba(14,58,43,0.97)] backdrop-blur-sm">
      {/* header */}
      <div className="flex items-center justify-between px-6 md:px-10 py-6 border-b border-[rgba(231,220,184,0.2)]">
        <div>
          {title && <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#cfc3a0]">{title}</div>}
          <div className="font-body text-sm text-[#d7cfbb] mt-1 tracking-[0.14em]">
            {current + 1} / {images.length} · <span className="text-[#e7dcb8]">click to zoom</span>
          </div>
        </div>
        <button onClick={onClose} className="w-11 h-11 border border-[rgba(231,220,184,0.3)] flex items-center justify-center text-[#f5f0e6] hover:text-white hover:border-[#e7dcb8] transition-colors" aria-label="Close">
          <X size={22} />
        </button>
      </div>

      {/* stage */}
      <div className="flex-1 relative flex items-center justify-center px-4 md:px-16 overflow-hidden">
        <button onClick={prev} className="absolute left-3 md:left-8 z-10 w-12 h-12 rounded-full border border-[rgba(231,220,184,0.3)] flex items-center justify-center text-[#f5f0e6] hover:text-white hover:border-[#e7dcb8] transition-colors bg-[rgba(14,58,43,0.5)]" aria-label="Previous">
          <ChevronLeft size={24} />
        </button>

        <div
          className="relative max-h-full max-w-5xl overflow-hidden cursor-zoom-in"
          style={{ cursor: zoom ? "zoom-out" : "zoom-in" }}
          onClick={() => setZoom((z) => !z)}
          onMouseMove={move}
        >
          <img
            src={images[current]}
            alt={`${title || "View"} ${current + 1}`}
            className="max-h-[74vh] w-auto object-contain select-none transition-transform duration-300 rounded"
            style={{ transform: zoom ? "scale(2.3)" : "scale(1)", transformOrigin: `${origin.x}% ${origin.y}%` }}
            draggable={false}
          />
          {!zoom && (
            <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-[rgba(14,58,43,0.8)] border border-[rgba(231,220,184,0.3)] px-3 py-2 text-[0.68rem] tracking-[0.14em] uppercase text-[#e7dcb8]">
              <ZoomIn size={13} /> Inspect
            </div>
          )}
        </div>

        <button onClick={next} className="absolute right-3 md:right-8 z-10 w-12 h-12 rounded-full border border-[rgba(231,220,184,0.3)] flex items-center justify-center text-[#f5f0e6] hover:text-white hover:border-[#e7dcb8] transition-colors bg-[rgba(14,58,43,0.5)]" aria-label="Next">
          <ChevronRight size={24} />
        </button>
      </div>

      {/* caption */}
      {captions[current] && (
        <div className="px-6 md:px-10 pb-1 text-center">
          <p className="font-serif-display text-lg md:text-xl text-[#f5f0e6] italic">{captions[current]}</p>
        </div>
      )}

      {/* thumbs */}
      <div className="px-6 md:px-10 py-6 border-t border-[rgba(231,220,184,0.2)]">
        <div className="flex gap-3 justify-center flex-wrap">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-16 h-16 md:w-20 md:h-20 overflow-hidden border transition-all rounded ${
                current === i ? "border-[#e7dcb8] opacity-100" : "border-[rgba(231,220,184,0.25)] opacity-50 hover:opacity-90"
              }`}
            >
              <img src={img} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
